<template>
    <NuxtLayout>
        <div class="lg:px-5 2xl:px-0">
            <ShcUiHeading :tag="HEADING_TAG.h1" class="mb-12 font-semibold !text-2xl">
                {{ $i18n.t('Bbk.storeLocatorPoint.pageTitle') }}
            </ShcUiHeading>

            <GoogleMapWrapper
                :mapClass="'w-full h-[410px] md:h-[600px] mb-8'"
                :placeHolderClass="'w-full h-[410px] md:h-[600px] mb-8 bg-bbk-grey'"
                :zoom="zoom"
                :center="center"
                :googleMapTheme="googleMapTheme"
                :markerList="storeLocatorPoints"
            />

            <div class="px-4 lg:px-0">
                <StoreLocatorSearch />

                <div
                    class="lg:flex lg:justify-start lg:items-start lg:flex-wrap lg:w-[calc(100%+32px)] lg:relative lg:left-[-16px]"
                >
                    <StoreLocatorPoint
                        v-for="point in storeLocatorPoints.slice(0, numberToSlice)"
                        :locationId="point.locationId"
                        :key="point.locationId"
                        :title="point.title"
                        :phone="point.phone"
                        :description="point.description"
                        :latitude="point.latitude"
                        :longitude="point.longitude"
                        :openTime="point.openTime"
                        :street="point.street"
                        :zip="point.zip"
                        :city="point.city"
                    />

                    <LazyStoreLocatorPoint
                        v-if="showAllPoints"
                        v-for="point in storeLocatorPoints.slice(numberToSlice, storeLocatorPoints.length)"
                        :locationId="point.locationId"
                        :key="point.locationId"
                        :title="point.title"
                        :phone="point.phone"
                        :description="point.description"
                        :latitude="point.latitude"
                        :longitude="point.longitude"
                        :openTime="point.openTime"
                        :street="point.street"
                        :zip="point.zip"
                        :city="point.city"
                    />
                </div>
            </div>

            <StoreLocatorPointSwitcher :showAllPoints="showAllPoints" :leftPoints="leftPoints" />
        </div>
    </NuxtLayout>
</template>

<script setup lang="ts">
    import { useWindowSize } from '@vueuse/core'
    import { useApolloClient } from '@vue/apollo-composable'
    import googleMapTheme from '../config/googleMapTheme'
    import getStoreLocatorPoints from '../graphql/queries/getStoreLocatorPoints.graphql'
    import type { StoreLocatorPoint } from '../types/storeLocatorPoint'
    import { HEADING_TAG } from '@shc/nuxt-shc-ui-layer/components/Heading/heading'

    definePageMeta({
        middleware: ['map-points'],
    })

    const { resolveClient } = useApolloClient()
    const { $i18n } = useNuxtApp()
    const client = resolveClient()
    const { $bus } = useNuxtApp()
    const { BREAKPOINTS } = useAppConfig()
    const { width } = useWindowSize()

    const zoom = ref(5)
    const center = ref({ lat: 52, lng: 20 })

    const storeLocatorPoints = useState<StoreLocatorPoint[]>('storeLocatorPoints')

    const numberToSlice = computed(() => {
        return width.value > BREAKPOINTS.lg ? 8 : 4
    })

    const leftPoints = computed(() => {
        return storeLocatorPoints.value.length - numberToSlice.value
    })

    let showAllPoints = ref(false)

    $bus.$on('toggleStoreLocatorAllPoints', () => {
        showAllPoints.value = !showAllPoints.value
    })

    $bus.$on('mapCitySearch', async ({ city, radius }) => {
        const geocoder = new google.maps.Geocoder()

        geocoder.geocode(
            {
                address: city,
            },
            async function (results, status) {
                if (status == google.maps.GeocoderStatus.OK) {
                    const { data } = await client.query({
                        query: getStoreLocatorPoints,
                        variables: {
                            searchNearby: {
                                radius: +radius,
                                longitude: results[0].geometry.location.lng(),
                                latitude: results[0].geometry.location.lat(),
                            },
                        },
                    })

                    storeLocatorPoints.value = data.commonStockLocations
                }
            },
        )
    })

    useHead({
        title: $i18n.t('Bbk.storeLocatorPoint.pageTitle'),
        meta: [
            {
                name: 'title',
                content: $i18n.t('Bbk.storeLocatorPoint.pageTitle'),
            },
            {
                name: 'robots',
                content: 'index, follow',
            },
        ],
    })
</script>
